class ProductVariantQuantitySelector {
  constructor(container) {
    this.container = container;
    this.productData = this.getProductData();
    this.selectedVariants = new Map();
    this.totalPrice = 0;

    this.init();
  }

  init() {
    this.bindEvents();

    this.restoreStateFromURL();

    this.syncSelectedVariantsFromInputs();
    const selectedVariant = this.ensureMinimumSelection();

    if (selectedVariant) {
      setTimeout(() => {
        this.filterAndShowMediaByVariant(selectedVariant.title);
      }, 100);
    }

    // 确保在设置完 selectedVariants 后再更新价格
    // 使用 setTimeout 确保 DOM 完全准备好
    setTimeout(() => {
      this.updateTotalPrice();
      this.updateProgramPrices();
    }, 0);
  }

  getProductData() {
    const scriptTag = this.container.querySelector("[data-product-data]");
    if (!scriptTag) {
      return null;
    }

    try {
      const data = JSON.parse(scriptTag.textContent);
      return data;
    } catch (error) {
      return null;
    }
  }

  restoreStateFromURL() {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const cartParam = urlParams.get("cart");

      if (cartParam) {
        sessionStorage.setItem("cartSelection", cartParam);
        window.location.reload();
        return;
      }

      const storedCartSelection = sessionStorage.getItem("cartSelection");
      if (storedCartSelection) {
        this.resetToInitialState();
        sessionStorage.removeItem("cartSelection");
        // 延迟更新价格，确保 selectedVariants 已设置
        setTimeout(() => {
          this.updateTotalPrice();
        }, 0);
      }
    } catch (error) {}
  }

  bindEvents() {
    this.container.addEventListener("click", (e) => {
      if (e.target.closest('[data-action="decrease"]')) {
        this.handleQuantityChange(
          e.target.closest('[data-action="decrease"]'),
          -1,
        );
      } else if (e.target.closest('[data-action="increase"]')) {
        this.handleQuantityChange(
          e.target.closest('[data-action="increase"]'),
          1,
        );
      }
    });

    this.container.addEventListener("input", (e) => {
      if (e.target.classList.contains("quantity-input")) {
        this.handleQuantityInput(e.target);
      }
    });

    this.container.addEventListener("click", (e) => {
      if (e.target.closest('[data-action="toggle-info"]')) {
        this.handleInfoToggle(e.target.closest('[data-action="toggle-info"]'));
      }
    });

    this.container.addEventListener("click", (e) => {
      if (e.target.closest('[data-action="preorder"]')) {
        this.handlePreorder();
      }
      if (e.target.closest('[data-action="add-to-cart"]')) {
        this.handleAddToCart();
      }
    });

    this.container
      .querySelectorAll(".variant-option .variant-image")
      .forEach((option) => {
        option.addEventListener("click", (e) => {
          this.handleVariantOptionImageClick(option);
        });
      });

    document.addEventListener("stickyBarBuyClick", (event) => {
      if (event.detail.productId === this.productData?.id) {
        this.handlePreorder();
      }
    });

    // 监听 Shopify section 重新加载（货币切换等情况）
    document.addEventListener("shopify:section:load", () => {
      // 重新计算并更新所有价格（包括 sale-price 和 program prices）
      this.syncSelectedVariantsFromInputs();
      this.ensureMinimumSelection();
      this.updateVariantOptions();
      this.updateTotalPrice();
    });

    window.addEventListener("pageshow", () => {
      this.syncSelectedVariantsFromInputs();
      this.ensureMinimumSelection();
      this.updateVariantOptions();
      this.updateTotalPrice();
    });
  }

  handleVariantOptionImageClick(option) {
    const variantId = option.dataset.variantId;
    const variant = this.productData.variants.find(
      (v) => v.id.toString() === variantId,
    );
    if (!variant) return;

    // 先执行基于alt属性的过滤功能
    const variantTitle = variant.title;
    this.filterAndShowMediaByVariant(variantTitle);

    // 直接在 media-gallery 上找到当前变体 + 当前设备下的第一张合法图片
    // 并通过 setActiveMedia 激活它（比缩略图点击更可靠，避免竞争覆盖）
    const mediaGallery = document.querySelector("media-gallery");
    if (mediaGallery) {
      const isDesktop = window.matchMedia("(min-width: 750px)").matches;
      const allVisibleSlides = mediaGallery.querySelectorAll(
        ".product__media-item:not(.hidden-by-variant)",
      );

      let targetSlide = null;
      for (const slide of allVisibleSlides) {
        // 跳过在当前设备上被 CSS 隐藏的图
        if (isDesktop) {
          if (
            slide.classList.contains("medium-hide") ||
            slide.classList.contains("large-up-hide")
          )
            continue;
        } else {
          if (slide.classList.contains("small-hide")) continue;
        }
        targetSlide = slide;
        break;
      }

      if (targetSlide) {
        const mediaId = targetSlide.getAttribute("data-media-id");
        if (mediaId) {
          mediaGallery.setActiveMedia(mediaId, false);

          // setActiveMedia 只管理 is-active 和缩略图同步，
          // 还需要通知 product-media-gallery.js 的 handleMediaChange 来启动自动播放/视频。
          // 通过 slideChanged 事件触发它。
          const sliderComponent = mediaGallery.querySelector(
            "product-media-slider",
          );
          if (sliderComponent) {
            sliderComponent.dispatchEvent(
              new CustomEvent("slideChanged", {
                detail: {
                  currentPage: 1,
                  currentElement: targetSlide,
                },
              }),
            );
          }
        }
      }
    }
  }

  filterAndShowMediaByVariant(selectedVariantTitle) {
    const mainSlider = document.querySelector(".slider-media-show");
    if (!mainSlider) return;

    const slides = mainSlider.querySelectorAll(".slider__slide");
    const filteredSlides = this.getFilteredSlides(slides, selectedVariantTitle);

    this.showFilteredSlides(slides, filteredSlides);
    this.updateThumbnailsForVariant(selectedVariantTitle);
  }

  getFilteredSlides(allSlides, selectedVariantTitle) {
    return Array.from(allSlides).filter((slide) => {
      const mediaElement = slide.querySelector("img, video");
      if (!mediaElement) return false;

      const altText = mediaElement.getAttribute("alt") || "";
      const altLower = altText.toLowerCase();

      if (altLower.includes("b test")) return false;

      const variantTitle = selectedVariantTitle || "";

      if (!variantTitle) {
        return true;
      }

      if (altText.toUpperCase().includes(variantTitle.toUpperCase())) {
        return true;
      }

      const isUnclassified = this.isUnclassifiedMedia(altText);
      return isUnclassified;
    });
  }

  isUnclassifiedMedia(altText) {
    if (!altText) return true;

    const allVariantTitles = this.productData.variants.map((v) => v.title);
    return !allVariantTitles.some((title) =>
      altText.toUpperCase().includes(title.toUpperCase()),
    );
  }

  showFilteredSlides(allSlides, filteredSlides) {
    // 只设置可见性，不管理激活状态（让原来的逻辑处理）
    allSlides.forEach((slide) => {
      const isVisible = filteredSlides.includes(slide);

      if (isVisible) {
        slide.classList.remove("hidden-by-variant");
      } else {
        slide.classList.add("hidden-by-variant");
      }
    });
  }

  scrollToSlide(targetSlide) {
    const mainSlider = document.querySelector(".slider-media-show");
    if (!mainSlider) return;

    const scrollContainer = mainSlider.querySelector('[id^="Slider-"]');
    if (scrollContainer) {
      const containerWidth = scrollContainer.clientWidth;
      const slideWidth = targetSlide.clientWidth;
      const slideLeft = targetSlide.offsetLeft;
      const scrollLeft = slideLeft - containerWidth / 2 + slideWidth / 2;

      scrollContainer.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior: "smooth",
      });

      mainSlider.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: 1,
            currentElement: targetSlide,
          },
        }),
      );
    }
  }

  updateThumbnailsForVariant(selectedVariantTitle) {
    const thumbnailSlider = document.querySelector(".thumbnail-slider");
    if (!thumbnailSlider) return;

    const thumbnailButtons = thumbnailSlider.querySelectorAll(
      ".thumbnail-list__item",
    );
    const filteredThumbnails = this.getFilteredThumbnails(
      thumbnailButtons,
      selectedVariantTitle,
    );

    // 只设置可见性，不管理激活状态（让原来的逻辑处理）
    thumbnailButtons.forEach((button) => {
      const isVisible = filteredThumbnails.includes(button);

      if (isVisible) {
        button.classList.remove("hidden-by-variant");
      } else {
        button.classList.add("hidden-by-variant");
      }
    });
  }

  getFilteredThumbnails(allThumbnails, selectedVariantTitle) {
    return Array.from(allThumbnails).filter((button) => {
      const img = button.querySelector("img");
      if (!img) return false;

      const altText = img.getAttribute("alt") || "";
      const altLower = altText.toLowerCase();

      if (altLower.includes("b test")) return false;

      const variantTitle = selectedVariantTitle || "";

      if (!variantTitle) {
        return true;
      }

      if (altText.toUpperCase().includes(variantTitle.toUpperCase())) {
        return true;
      }

      const isUnclassified = this.isUnclassifiedMedia(altText);
      return isUnclassified;
    });
  }

  handleQuantityChange(button, change) {
    const variantId = button.dataset.variantId;
    const input = this.container.querySelector(
      `input[data-variant-id="${variantId}"]`,
    );

    if (!input) return;

    this.syncSelectedVariantsFromInputs();
    this.ensureMinimumSelection();

    let newQuantity = this.normalizeQuantity(input.value) + change;
    newQuantity = Math.max(0, newQuantity);

    const hasOtherVariantsWithQuantity =
      this.hasOtherVariantsWithQuantity(variantId);

    if (newQuantity === 0 && !hasOtherVariantsWithQuantity) {
      input.value = 1;
      this.updateVariantQuantity(variantId, 1);
      return;
    }

    input.value = newQuantity;
    this.updateVariantQuantity(variantId, newQuantity);
  }

  handleQuantityInput(input) {
    const variantId = input.dataset.variantId;
    let quantity = this.normalizeQuantity(input.value);

    const hasOtherVariantsWithQuantity =
      this.hasOtherVariantsWithQuantity(variantId);

    if (quantity === 0 && !hasOtherVariantsWithQuantity) {
      quantity = 1;
    }

    input.value = quantity;
    this.updateVariantQuantity(variantId, quantity);
  }

  hasOtherVariantsWithQuantity(currentVariantId) {
    const inputs = this.container.querySelectorAll(".quantity-input");
    for (const input of inputs) {
      const inputVariantId = input.dataset.variantId;
      const inputQuantity = this.normalizeQuantity(input.value);
      if (inputVariantId !== currentVariantId && inputQuantity > 0) {
        return true;
      }
    }

    return false;
  }

  updateVariantQuantity(variantId, quantity) {
    if (quantity === 0) {
      this.selectedVariants.delete(variantId);
    } else {
      this.selectedVariants.set(variantId, quantity);
    }

    this.updateVariantOptions();
    this.updateTotalPrice();
  }

  normalizeQuantity(value) {
    const quantity = parseInt(value, 10);
    return Number.isFinite(quantity) ? Math.max(0, quantity) : 0;
  }

  syncSelectedVariantsFromInputs() {
    this.selectedVariants.clear();

    const inputs = this.container.querySelectorAll(".quantity-input");
    inputs.forEach((input) => {
      const variantId = input.dataset.variantId;
      const quantity = this.normalizeQuantity(input.value);

      input.value = quantity;

      if (variantId && quantity > 0) {
        this.selectedVariants.set(variantId, quantity);
      }
    });
  }

  ensureMinimumSelection() {
    for (const [variantId, quantity] of this.selectedVariants.entries()) {
      if (quantity > 0) {
        return this.productData?.variants.find(
          (variant) => variant.id.toString() === variantId,
        );
      }
    }

    const defaultInput = this.getDefaultQuantityInput();
    if (!defaultInput) {
      return null;
    }

    const defaultVariantId = defaultInput.dataset.variantId;
    defaultInput.value = 1;

    if (defaultVariantId) {
      this.selectedVariants.set(defaultVariantId, 1);
    }

    return this.productData?.variants.find(
      (variant) => variant.id.toString() === defaultVariantId,
    );
  }

  getDefaultQuantityInput() {
    return (
      this.container.querySelector(
        '.quantity-input[data-is-default-quantity-variant="true"]',
      ) || this.container.querySelector(".quantity-input")
    );
  }

  updateTotalPrice() {
    if (!this.productData || !Array.isArray(this.productData.variants)) {
      return;
    }

    this.totalPrice = 0;
    let totalCompareAtPrice = 0;
    let totalQuantity = 0;
    let totalRegularPrice = 0;
    let hasDiscountedPercentLine = false;

    const isPriceActivityType = this.productData.activity_type === "price";
    const isTieredPercentActivityType =
      this.productData.activity_type === "tiered_percent";
    const isPercentActivityType = this.productData.activity_type === "percent";
    const isBundleDiscountActivityType =
      this.productData.activity_type === "bundle_discount";

    this.selectedVariants.forEach((quantity, variantId) => {
      const variant = this.productData.variants.find(
        (v) => v.id.toString() === variantId,
      );
      const lineQuantity = parseInt(quantity) || 0;

      if (variant && variant.price && lineQuantity > 0) {
        let priceToUse = variant.price;
        let compareAtPriceToUse = variant.compare_at_price;
        const regularPriceInDollars = parseFloat(variant.price) / 100;

        if (!isNaN(regularPriceInDollars)) {
          totalRegularPrice += regularPriceInDollars * lineQuantity;
        }

        if (this.productData.show_activity_text) {
          if (isPriceActivityType || isBundleDiscountActivityType) {
            priceToUse = variant.price;
            compareAtPriceToUse = compareAtPriceToUse || variant.price;
          } else if (isTieredPercentActivityType) {
            priceToUse = variant.price;
            compareAtPriceToUse = compareAtPriceToUse || variant.price;
          } else if (this.variantHasActivity(variant)) {
            priceToUse =
              variant.activity_price ??
              this.productData.customer_current_price ??
              variant.price;
            compareAtPriceToUse = variant.price;
            hasDiscountedPercentLine = true;
          }
        }

        const priceInDollars = parseFloat(priceToUse) / 100;
        if (!isNaN(priceInDollars)) {
          const itemTotal = priceInDollars * lineQuantity;
          this.totalPrice += itemTotal;
          totalQuantity += lineQuantity;
        }

        if (compareAtPriceToUse) {
          const compareAtPriceInDollars = parseFloat(compareAtPriceToUse) / 100;
          if (!isNaN(compareAtPriceInDollars)) {
            const itemCompareAtTotal = compareAtPriceInDollars * lineQuantity;
            totalCompareAtPrice += itemCompareAtTotal;
          }
        }
      }
    });

    if (isPercentActivityType && hasDiscountedPercentLine) {
      totalCompareAtPrice = totalRegularPrice;
    }

    let finalTotalPrice = this.totalPrice;
    let saveAmount = 0;

    if (isBundleDiscountActivityType) {
      // Configured amounts are USD; convert with the same rate used by other activities
      const unitCurrent = this.convertUsdPriceToActiveCurrency(
        this.getVariantPriceInDollars(
          { price: this.productData.bundle_current_price },
          "price",
          0,
        ),
      );
      const unitOriginal = this.convertUsdPriceToActiveCurrency(
        this.getVariantPriceInDollars(
          { price: this.productData.bundle_original_price },
          "price",
          0,
        ),
      );
      const quantityMultiplier = Math.max(totalQuantity, 0);
      finalTotalPrice = unitCurrent * quantityMultiplier;
      totalCompareAtPrice = unitOriginal * quantityMultiplier;
      saveAmount = Math.max(0, totalCompareAtPrice - finalTotalPrice);
      this.totalPrice = finalTotalPrice;
      this.updateSaveDisplay(saveAmount);
      this.updateBundleValueDisplay(totalCompareAtPrice);
    } else if (
      this.productData.activity_type === "price" &&
      this.productData.discount_price
    ) {
      if (totalQuantity > 0) {
        saveAmount = this.calculateSaveAmount(totalQuantity);
        finalTotalPrice = this.totalPrice - saveAmount;
      }
      finalTotalPrice = Math.max(0, finalTotalPrice);
      this.updateSaveDisplay(saveAmount);
    } else if (isTieredPercentActivityType) {
      finalTotalPrice = 0;

      const useTieredPrice = totalQuantity >= 2;

      this.selectedVariants.forEach((quantity, variantId) => {
        const variant = this.productData.variants.find(
          (v) => v.id.toString() === variantId,
        );
        const lineQuantity = parseInt(quantity) || 0;

        if (variant && variant.price && lineQuantity > 0) {
          const priceInDollars = this.getVariantPriceInDollars(
            variant,
            useTieredPrice ? "tiered_activity_price" : "activity_price",
            variant.price,
          );

          if (!isNaN(priceInDollars)) {
            finalTotalPrice += priceInDollars * lineQuantity;
          }
        }
      });

      saveAmount = this.totalPrice - finalTotalPrice;
      saveAmount = Math.max(0, saveAmount);
      this.updateSaveDisplay(saveAmount);
    } else {
      this.updateSaveDisplay(0);
    }

    finalTotalPrice = Math.max(0, finalTotalPrice);

    // 更新 sale-price 显示
    // 优先查找 .sale-price[data-total-price]，确保找到正确的元素
    const priceElement =
      this.container.querySelector(".sale-price[data-total-price]") ||
      this.container.querySelector("[data-total-price]");
    if (priceElement) {
      const formattedPrice = this.formatPrice(finalTotalPrice);
      priceElement.textContent = formattedPrice;
      priceElement.dataset.totalPrice = formattedPrice;
    } else {
      // 如果找不到元素，尝试在容器内的所有 sale-price 元素中查找
      const allSalePrices = this.container.querySelectorAll(".sale-price");
      if (allSalePrices.length > 0) {
        const firstSalePrice = allSalePrices[0];
        const formattedPrice = this.formatPrice(finalTotalPrice);
        firstSalePrice.textContent = formattedPrice;
        if (!firstSalePrice.hasAttribute("data-total-price")) {
          firstSalePrice.setAttribute("data-total-price", formattedPrice);
        }
      } else {
        console.warn(
          "ProductVariantQuantitySelector: Could not find sale-price element to update",
        );
      }
    }

    const compareAtPriceElement = this.container.querySelector(
      ".original-price-amount",
    );
    const shouldShowCompareAtPrice =
      !isBundleDiscountActivityType &&
      totalCompareAtPrice > 0 &&
      totalCompareAtPrice > finalTotalPrice;

    if (compareAtPriceElement) {
      const parentOriginalPriceElement =
        compareAtPriceElement.closest(".original-price");

      if (shouldShowCompareAtPrice) {
        const formattedCompareAtPrice = this.formatPrice(totalCompareAtPrice);
        compareAtPriceElement.textContent = formattedCompareAtPrice;
        parentOriginalPriceElement?.classList.remove("hidden");
      } else {
        compareAtPriceElement.textContent = "";
        parentOriginalPriceElement?.classList.add("hidden");
      }
    }

    // 同时更新 program 价格（使用相同的货币格式化逻辑）
    this.updateProgramPrices();

    const priceUpdateEvent = new CustomEvent("productPriceUpdated", {
      detail: {
        totalPrice: finalTotalPrice,
        originalTotalPrice: this.totalPrice,
        totalCompareAtPrice: totalCompareAtPrice,
        saveAmount: saveAmount,
        activity_type: this.productData?.activity_type || null,
        formattedPrice: this.formatPrice(finalTotalPrice),
        formattedCompareAtPrice:
          shouldShowCompareAtPrice || isBundleDiscountActivityType
            ? this.formatPrice(totalCompareAtPrice)
            : null,
        discountPercentage: shouldShowCompareAtPrice
          ? Math.round(
              ((totalCompareAtPrice - finalTotalPrice) / totalCompareAtPrice) *
                100,
            )
          : null,
        selectedVariants: Array.from(this.selectedVariants.entries()),
      },
    });
    document.dispatchEvent(priceUpdateEvent);
  }

  variantHasActivity(variant) {
    return Boolean(
      this.productData?.show_activity_text &&
        this.productData?.activity_type === "percent" &&
        variant?.is_activity_variant,
    );
  }

  getVariantPriceInDollars(variant, key, fallback = 0) {
    const value = variant?.[key] ?? fallback;
    const priceInCents = parseFloat(value);

    if (isNaN(priceInCents) || !isFinite(priceInCents)) {
      return 0;
    }

    return priceInCents / 100;
  }

  calculateSaveAmount(totalQuantity) {
    if (!this.productData.discount_price || totalQuantity <= 1) {
      return 0;
    }

    const discountMultiplier = Math.floor(totalQuantity / 2);
    const discountPriceInUsd = parseFloat(this.productData.discount_price) / 100;
    const discountPriceInActiveCurrency =
      this.convertUsdPriceToActiveCurrency(discountPriceInUsd);

    return discountMultiplier * discountPriceInActiveCurrency;
  }

  updateSaveDisplay(saveAmount) {
    const saveElement = this.container.querySelector("[data-save-amount]");
    if (saveElement) {
      if (saveAmount === 0) {
        saveElement.classList.add("hidden");
      } else {
        saveElement.classList.remove("hidden");
      }
      const formattedSave = this.formatPrice(saveAmount);
      if (this.productData?.activity_type === "bundle_discount") {
        saveElement.textContent = `Save ${formattedSave}`;
      } else {
        saveElement.textContent = `save${formattedSave}`;
      }
    }
  }

  updateBundleValueDisplay(valueAmount) {
    const valueElement = this.container.querySelector("[data-bundle-value]");
    const valueContainer = this.container.querySelector(".bundle-value-price");
    if (!valueElement) return;

    if (!valueAmount || valueAmount <= 0) {
      valueElement.textContent = "";
      valueContainer?.classList.add("hidden");
      return;
    }

    valueElement.textContent = this.formatPrice(valueAmount);
    valueContainer?.classList.remove("hidden");
  }

  formatPrice(price) {
    if (isNaN(price) || !isFinite(price)) {
      return "$0.00";
    }

    return new Intl.NumberFormat(window.Shopify.locale, {
      style: "currency",
      currency: window.Shopify.currency.active,
    }).format(price);
  }

  convertUsdPriceToActiveCurrency(price) {
    if (isNaN(price) || !isFinite(price)) {
      return 0;
    }

    const rate = parseFloat(window.Shopify?.currency?.rate || "1");
    return price * (isNaN(rate) ? 1 : rate);
  }

  handleInfoToggle(infoLink) {
    const infoType = infoLink.dataset.infoType;

    if (infoType === "box") {
      this.toggleWhatsOnTheBoxContent(infoLink);
    }
  }

  toggleWhatsOnTheBoxContent(infoLink) {
    const content = this.container.querySelector("#whats-on-the-box-content");
    const arrow = infoLink.querySelector(".info-arrow svg");

    if (!content || !this.productData.whats_on_the_box) {
      return;
    }

    const isExpanded = content.style.display !== "none";

    if (isExpanded) {
      content.style.display = "none";
      if (arrow) {
        arrow.style.transform = "rotate(0deg)";
      }
    } else {
      this.showWhatsOnTheBoxContent(content);
      if (arrow) {
        arrow.style.transform = "rotate(90deg)";
      }
    }
  }

  showWhatsOnTheBoxContent(content) {
    const image = content.querySelector(".whats-on-the-box-image");

    if (!image) {
      return;
    }

    const isMobile = window.innerWidth <= 768;
    const imageUrl = isMobile
      ? this.productData.whats_on_the_box.mobile
      : this.productData.whats_on_the_box.desktop;

    const finalImageUrl = imageUrl || this.productData.whats_on_the_box.desktop;

    if (!finalImageUrl) {
      return;
    }

    image.src = finalImageUrl;
    image.dataset.desktopSrc = this.productData.whats_on_the_box.desktop || "";
    image.dataset.mobileSrc = this.productData.whats_on_the_box.mobile || "";

    content.style.display = "block";

    this.handleExpandedImageResponsive(image);
  }

  handleExpandedImageResponsive(image) {
    const updateImage = () => {
      const isMobile = window.innerWidth <= 768;
      const imageUrl = isMobile
        ? image.dataset.mobileSrc
        : image.dataset.desktopSrc;

      const finalImageUrl = imageUrl || image.dataset.desktopSrc;

      if (finalImageUrl && image.src !== finalImageUrl) {
        image.src = finalImageUrl;
      }
    };

    window.addEventListener("resize", updateImage);
    this.expandedImageResizeHandler = updateImage;
  }

  async handleAddToCart() {
    this.syncSelectedVariantsFromInputs();
    this.ensureMinimumSelection();
    this.updateVariantOptions();

    if (this.selectedVariants.size === 0) {
      return;
    }

    // 获取购物车侧边栏元素
    const cartDrawer = document.querySelector("cart-drawer");
    const cartNotification = document.querySelector("cart-notification");
    const cart = cartDrawer || cartNotification;

    if (!cart) {
      console.error("Cart drawer or notification not found");
      return;
    }

    // 准备批量添加的商品数据
    const items = [];
    this.selectedVariants.forEach((quantity, variantId) => {
      items.push({
        id: parseInt(variantId),
        quantity: quantity,
      });
    });

    try {
      // 显示加载状态
      const addToCartBtn = this.container.querySelector(
        '[data-action="add-to-cart"]',
      );
      if (addToCartBtn) {
        addToCartBtn.classList.add("loading");
      }

      // 调用 Shopify Cart API 批量添加商品
      const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ items }),
      });

      const result = await response.json();

      if (response.ok) {
        // 获取需要更新的 sections
        const sectionsToFetch = cart
          .getSectionsToRender()
          .map((section) => section.id);

        // 获取更新后的购物车内容
        const sectionsResponse = await fetch(
          `${window.location.pathname}?sections=${sectionsToFetch.join(",")}`,
        );
        const sections = await sectionsResponse.json();

        // 构建渲染数据
        const parsedState = {
          sections: sections,
          id: this.productData?.id,
        };

        // 发布购物车更新事件
        if (
          typeof publish !== "undefined" &&
          typeof PUB_SUB_EVENTS !== "undefined"
        ) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "product-variant-quantity-selector",
            cartData: result,
          });
        }

        cart.classList.remove("is-empty");

        // 渲染购物车内容并打开侧边栏
        if (cart.renderContents) {
          cart.renderContents(parsedState);
        } else {
          // 如果没有 renderContents 方法，直接打开
          if (cart.open) {
            cart.open();
          }
        }

        // 发送分析事件
        if (typeof Shopify !== "undefined" && Shopify.analytics) {
          Shopify.analytics.publish("dex:add-to-cart-click");
        }
      } else {
        // 处理错误
        console.error("添加到购物车失败:", result);
        const errorMessage =
          result.description || result.message || "添加到购物车失败，请重试";
        alert(errorMessage);
      }
    } catch (error) {
      console.error("添加到购物车错误:", error);
      alert("添加到购物车时出错，请重试");
    } finally {
      // 恢复按钮状态
      const addToCartBtn = this.container.querySelector(
        '[data-action="add-to-cart"]',
      );
      if (addToCartBtn) {
        addToCartBtn.classList.remove("loading");
      }
    }
  }

  handlePreorder() {
    this.syncSelectedVariantsFromInputs();
    this.ensureMinimumSelection();
    this.updateVariantOptions();

    if (this.selectedVariants.size === 0) {
      return;
    }

    const cartItems = [];
    this.selectedVariants.forEach((quantity, variantId) => {
      cartItems.push({
        id: parseInt(variantId),
        quantity: quantity,
      });
    });

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({'event': 'PreorderNowClick'});
    // Shopify.analytics.publish("dex:preorder-now-click");

    setTimeout(() => {
      this.directCheckout(cartItems);
    }, 300)
  }

  async directCheckout(cartItems) {
    try {
      const currentUrl = new URL(window.location.href);

      let cartUrl = currentUrl.origin + "/cart/";

      const cartItemsArray = [];
      cartItems.forEach((item) => {
        cartItemsArray.push(`${item.id}:${item.quantity}`);
      });
      cartUrl += cartItemsArray.join(",");

      // 获取当前页面的URL参数
      const currentUrlParams = new URLSearchParams(window.location.search);

      // 构建新的URL参数（checkout 无等号，保持为 flag 格式）
      const params = ["checkout"]; // 使用 flag 形式的 checkout

      // 传递当前页面的所有参数（排除 cart 与 checkout，避免重复或冲突）
      const newCartUrl = new URL(cartUrl);
      currentUrlParams.forEach((value, key) => {
        if (key !== "cart" && key !== "checkout") {
          newCartUrl.searchParams.set(key, value);
        }
      });

      // Intelligems: JS cart permalink 需手动带上 visitor igId，否则订单无法归因到实验组
      const igId = window.igData?.user?.igId;
      if (igId) {
        newCartUrl.searchParams.set("attributes[igId]", igId);
      }

      window.location.href = newCartUrl.toString();
    } catch (error) {
      console.error("Direct checkout error:", error);
      window.location.href = "/cart";
    }
  }

  getSelectedVariantsSummary() {
    const summary = [];

    this.selectedVariants.forEach((quantity, variantId) => {
      const variant = this.productData.variants.find(
        (v) => v.id.toString() === variantId,
      );
      if (variant) {
        summary.push({
          id: variant.id,
          title: variant.title,
          quantity: quantity,
          price: variant.price / 100,
        });
      }
    });

    return summary;
  }

  reset() {
    this.selectedVariants.clear();

    const inputs = this.container.querySelectorAll(".quantity-input");
    inputs.forEach((input) => {
      input.value = 0;
    });

    this.updateTotalPrice();
  }

  resetToInitialState() {
    this.selectedVariants.clear();

    const inputs = this.container.querySelectorAll(".quantity-input");
    inputs.forEach((input) => {
      input.value = 0;
    });

    if (this.productData && this.productData.variants.length > 0) {
      const defaultInput = this.getDefaultQuantityInput();
      const defaultVariantId = defaultInput?.dataset.variantId;

      if (defaultInput && defaultVariantId) {
        this.selectedVariants.set(defaultVariantId, 1);
        defaultInput.value = 1;
      }
    }

    this.updateVariantOptions();
  }

  updateVariantOptions() {
    const variantOptions = this.container.querySelectorAll(".variant-option");
    variantOptions.forEach((option) => {
      const variantId =
        option.querySelector(".quantity-input")?.dataset.variantId;
      if (variantId) {
        const quantity = this.selectedVariants.get(variantId) || 0;
        const input = option.querySelector(".quantity-input");

        if (input) {
          input.value = quantity;

          if (quantity > 0) {
            option.classList.add("selected");
          } else {
            option.classList.remove("selected");
          }
        }
      }
    });
  }

  updateProgramPrices() {
    // 更新 program 价格以支持货币切换
    const programPriceContainer = this.container.querySelector(
      ".product__program-content-price",
    );
    if (!programPriceContainer) return;

    const programPrice = programPriceContainer.dataset.programPrice;
    const programOriginalPrice =
      programPriceContainer.dataset.programOriginalPrice;

    if (programPrice && programPrice !== "0") {
      const priceInDollars = parseFloat(programPrice) / 100;
      const formattedPrice = this.formatPrice(priceInDollars);

      const priceElement =
        programPriceContainer.querySelector(".money:first-child");
      if (priceElement) {
        priceElement.textContent = formattedPrice;
      }
    }

    if (programOriginalPrice && programOriginalPrice !== "0") {
      const originalPriceInDollars = parseFloat(programOriginalPrice) / 100;
      const formattedOriginalPrice = this.formatPrice(originalPriceInDollars);

      const originalPriceElement = programPriceContainer.querySelector(
        ".product__program-original-price",
      );
      if (originalPriceElement) {
        originalPriceElement.textContent = formattedOriginalPrice;
      }
    }
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const selectors = document.querySelectorAll(
    ".product-variant-quantity-selector",
  );
  selectors.forEach((selector) => {
    new ProductVariantQuantitySelector(selector);
  });
});

if (typeof module !== "undefined" && module.exports) {
  module.exports = ProductVariantQuantitySelector;
}
